import { useQuery } from '@tanstack/react-query';

import { fetcher } from '@/utils/fetcher';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useCurrentAuthSession } from '@/context/AuthContext';
import { getWindowLogger } from '@/utils/remoteLogger';
import { LogAction, LogStatus } from '@/types/Logging';
import { fsPath } from './__path';

const logger = getWindowLogger({
  file: `${fsPath}/useUserInfo.ts`,
});

export type UserInfo = {
  readonly customer: string;
  readonly accessLevel: number;
};

const currentUser = {
  _uid: 'no-uid',
  _result: {} as UserInfo,
  get uid() {
    return this._uid;
  },
  set uid(value: string) {
    this._uid = value;
  },
  get result(): UserInfo {
    return this._result;
  },
  set result(value: UserInfo) {
    this._result = value;
  },
};

export const useUserInfo = () => {
  const { user } = useCurrentAuthSession();

  const { data, isLoading } = useQuery({
    queryKey: ['read-users', user?.uid],
    queryFn: async () => {
      if (user?.uid === currentUser.uid) {
        return currentUser.result;
      }

      const result = await fetcher<UserInfo>(
        `${RETAIL_API_URL}/users/read?userId=${user?.uid}`,
      );

      currentUser.uid = user?.uid ?? 'no-uid';
      currentUser.result = {
        customer: result.customer,
        accessLevel: result.accessLevel,
      };

      logger.info({
        action: LogAction.READ,
        status: LogStatus.SUCCEEDED,
        user: user?.uid ?? 'unkown-user',
        customer: result.customer,
        data: { accessLevel: result.accessLevel },
      });

      return result;
    },
    enabled: Boolean(user?.uid),
  });

  const isAdmin = data?.accessLevel === 90;

  return { data, isLoading, isAdmin };
};
